/* Dawn keeps ownership of variant resolution, availability and cart submission. */
if (!customElements.get('aromafit-size-gate')) {
  customElements.define('aromafit-size-gate', class extends HTMLElement {
    connectedCallback() {
      this.productInfo = this.closest('product-info');
      if (!this.productInfo || this.observer) return;
      this.chosenSize = null;
      this.pendingPicker = null;
      this.currentPicker = null;
      this.paymentStates = new WeakMap();
      this.onChange = this.handleChange.bind(this);
      this.onSubmit = this.handleSubmit.bind(this);
      this.productInfo.addEventListener('change', this.onChange, true);
      this.productInfo.addEventListener('submit', this.onSubmit, true);
      this.observer = new MutationObserver(() => this.reconcile());
      this.observer.observe(this.productInfo, { childList: true, subtree: true });
      this.reconcile();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.productInfo?.removeEventListener('change', this.onChange, true);
      this.productInfo?.removeEventListener('submit', this.onSubmit, true);
      this.observer = null;
    }

    getSizeControl(picker) {
      if (!picker) return null;
      const groups = picker.querySelectorAll('fieldset, .product-form__input--dropdown');
      return groups[Number(this.dataset.optionPosition) - 1] || null;
    }

    getSizeValue(group) {
      const select = group?.querySelector('select');
      return select ? select.value : group?.querySelector('input:checked')?.value;
    }

    describeControl(group) {
      group?.querySelectorAll('input, select').forEach((input) => {
        const ids = new Set((input.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));
        ids.add(this.dataset.statusId);
        input.setAttribute('aria-describedby', Array.from(ids).join(' '));
      });
    }

    clearDefaultSelection(group) {
      if (!group) return;
      const select = group.querySelector('select');
      if (select) {
        if (select.querySelector('[data-aromafit-placeholder]')) return;
        const selected = select.selectedOptions[0];
        const placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = this.dataset.chooseLabel;
        placeholder.disabled = true;
        placeholder.setAttribute('data-aromafit-placeholder', '');
        // Dawn expects one selected option attribute, including during other-option changes.
        // Its internal candidate is never purchasable while this gate is locked.
        if (selected?.dataset.optionValueId) placeholder.dataset.optionValueId = selected.dataset.optionValueId;
        if (selected?.dataset.productUrl) placeholder.dataset.productUrl = selected.dataset.productUrl;
        select.querySelectorAll('option[selected]').forEach((option) => option.removeAttribute('selected'));
        placeholder.setAttribute('selected', 'selected');
        select.prepend(placeholder);
        select.value = '';
      } else {
        group.querySelectorAll('input[type="radio"]').forEach((input) => {
          input.checked = false;
          input.removeAttribute('checked');
        });
      }
      const selectedLabel = group.querySelector('[data-selected-value]');
      if (selectedLabel) selectedLabel.textContent = '';
    }

    handleChange(event) {
      const picker = this.productInfo.querySelector('variant-selects');
      if (!picker?.contains(event.target)) return;
      const group = this.getSizeControl(picker);
      if (group?.contains(event.target)) {
        this.chosenSize = this.getSizeValue(group) || null;
      }
      this.pendingPicker = picker;
      this.setGate(false, this.chosenSize ? this.dataset.updatingLabel : this.dataset.chooseLabel);
    }

    handleSubmit(event) {
      // The native hidden input named "id" shadows HTMLFormElement.id.
      if (event.target.getAttribute('id') !== this.dataset.formId || this.productInfo.dataset.aromafitPurchaseReady === 'true') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const picker = this.productInfo.querySelector('variant-selects');
      this.getSizeControl(picker)?.querySelector('select, input:not([disabled])')?.focus();
    }

    reconcile() {
      const picker = this.productInfo.querySelector('variant-selects');
      const group = this.getSizeControl(picker);
      if (!picker || !group) {
        this.setGate(false, this.dataset.chooseLabel);
        return;
      }
      if (picker !== this.currentPicker) {
        this.currentPicker = picker;
        this.describeControl(group);
        if (this.pendingPicker && this.pendingPicker !== picker) this.pendingPicker = null;
        if (!this.chosenSize) this.clearDefaultSelection(group);
        // A product/option change must never silently substitute a different size.
        else if (this.getSizeValue(group) !== this.chosenSize) {
          this.chosenSize = null;
          this.clearDefaultSelection(group);
        }
      }
      let variant = null;
      try { variant = JSON.parse(picker.querySelector('[data-selected-variant]')?.textContent || 'null'); }
      catch (_) { /* Fail closed if a third-party customization removes Dawn's variant data. */ }
      const confirmed = !!this.chosenSize && !this.pendingPicker;
      const available = !!variant?.available;
      const submit = this.productInfo.querySelector(`#${CSS.escape(this.dataset.formId)} [name="add"]`);
      const ready = confirmed && available && !!submit && !submit.disabled;
      const message = !this.chosenSize ? this.dataset.chooseLabel
        : this.pendingPicker ? this.dataset.updatingLabel
        : !available ? this.dataset.unavailableLabel
        : this.dataset.selectedLabel.replace('[size]', this.chosenSize);
      this.setGate(ready, message);
    }

    setGate(ready, message) {
      this.productInfo.dataset.aromafitPurchaseReady = String(ready);
      this.productInfo.querySelectorAll('[data-aromafit-purchase-gate]').forEach((gate) => {
        gate.disabled = !ready;
      });
      // Shopify accelerated buttons can render within shadow DOM: fieldset alone is insufficient.
      this.productInfo.querySelectorAll('.shopify-payment-button, shopify-accelerated-checkout').forEach((payment) => {
        if (!ready) {
          if (!this.paymentStates.has(payment)) {
            this.paymentStates.set(payment, {
              inert: payment.inert,
              hidden: payment.hidden,
              ariaHidden: payment.getAttribute('aria-hidden'),
            });
          }
          payment.inert = true;
          payment.hidden = true;
          payment.setAttribute('aria-hidden', 'true');
        } else if (this.paymentStates.has(payment)) {
          const original = this.paymentStates.get(payment);
          payment.inert = original.inert;
          payment.hidden = original.hidden;
          if (original.ariaHidden === null) payment.removeAttribute('aria-hidden');
          else payment.setAttribute('aria-hidden', original.ariaHidden);
          this.paymentStates.delete(payment);
        }
      });
      const status = this.querySelector('[data-aromafit-size-status]');
      if (status && status.textContent.trim() !== message) status.textContent = message;
    }
  });
}
